import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { isValidLink } from "../../../../helpers/urlRegexValidator";
import { createCientServiceSubscriptionTaskChecklistItem } from "../../../../services/clientServiceSubscriptionTaskChecklistItem/createCientServiceSubscriptionTaskChecklistItem";
import InputTextModal from "../../modalGeneralComponents/InputTextModal";
import ButtonModal from "../../updateClientServiceSubscriptionTaskModal/ButtonModal";
import { usePermissions } from "../../../../context/PermissionsContext";

const AddNewClientServiceSubscriptionTaskChecklistItem = ({
    open,
    setOpen,
    taskId,
    getNewChecklistItems,
    loggedInEmployee,
    employees,
    updateClientService,
    getNewJobs,
}) => {
    const { can, permissionsLoading } = usePermissions();
    const canWriteSteps = can("steps.write");
    const completeButtonRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [displayOptionalFields, setDisplayOptionalFields] = useState({
        notes: false,
        links: false,
    });

    const toggleLinks = () =>
        setDisplayOptionalFields((prev) => ({
            ...prev,
            links: !prev.links,
        }));

    const [newLinkUrl, setNewLinkUrl] = useState("");

    // NOTE: we keep isPermanent boolean in the payload for backend compatibility.
    const [newChecklistItem, setNewChecklistItem] = useState({
        description: "",
        links: [],
        notes: "",
        assignedTo: null,
        assignedDate: new Date().toISOString().split("T")[0],
        dueDate: new Date().toISOString().split("T")[0],
        isPermanent: false,
    });

    // NEW: local select value to enforce HTML "required" without changing backend shape
    const [renewalOption, setRenewalOption] = useState(""); // "" | "temporary" | "permanent"

    const onHandleChange = (e) => {
        const { name, value } = e.target;
        setNewChecklistItem((prev) => ({ ...prev, [name]: value }));
    };

    // NEW: map dropdown to boolean
    const onHandleChangeRenewal = (e) => {
        const value = e.target.value; // "temporary" | "permanent"
        setRenewalOption(value);
        setNewChecklistItem((prev) => ({
            ...prev,
            isPermanent: value === "permanent",
        }));
    };

    const handleChangeLink = (e) => {
        const url = e.target.value;
        setNewLinkUrl(url);
        const linkObj = { name: url, url };
        setNewChecklistItem((prev) => ({
            ...prev,
            links: url.trim() ? [linkObj] : [],
        }));
    };

    const resetForm = () => {
        setNewChecklistItem({
            description: "",
            links: [],
            notes: "",
            assignedTo: null,
            assignedDate: new Date().toISOString().split("T")[0],
            dueDate: new Date().toISOString().split("T")[0],
            isPermanent: false,
        });
        setRenewalOption(""); // reset dropdown
        setNewLinkUrl("");
        setDisplayOptionalFields({ notes: false, links: false });
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (permissionsLoading || !canWriteSteps || isSaving) return;

        if (!newChecklistItem.description.trim()) {
            toast.error("Name is required.");
            return;
        }
        if (renewalOption === "") {
            toast.error("Please choose Temporary or Permanent.");
            return;
        }
        if (newLinkUrl.trim() && !isValidLink(newLinkUrl)) {
            toast.error("Please enter a valid URL.");
            return;
        }

        setIsSaving(true);
        try {
            const response =
                await createCientServiceSubscriptionTaskChecklistItem(
                    taskId,
                    newChecklistItem,
                );

            if (response.data.success) {
                toast.success(response.data.message);
                updateClientService();
                await getNewJobs();
                await getNewChecklistItems(false);
                resetForm();
                setOpen(false);
            } else if (response.status !== 403) {
                toast.error(response.data.message);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const onHandleCancel = () => {
        resetForm();
        setOpen(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={onHandleCancel}
                    className="relative z-50"
                >
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
                        <Dialog.Panel className="relative w-full max-w-lg bg-white rounded-lg shadow-xl">
                            <div className="flex items-center justify-between p-4 border-b">
                                <Dialog.Title className="text-lg font-medium text-gray-900">
                                    Add Custom Checklist Item
                                </Dialog.Title>
                                <button
                                    onClick={onHandleCancel}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    ✕
                                </button>
                            </div>

                            <form
                                onSubmit={onHandleSubmit}
                                className="p-6 space-y-6"
                            >
                                <InputTextModal
                                    required
                                    label="Name"
                                    name="description"
                                    onChange={onHandleChange}
                                    value={newChecklistItem.description}
                                />

                                {/* REPLACED: Checkbox -> Dropdown (required) */}
                                <div className="mt-2">
                                    <label className="block mb-1 text-sm font-medium text-gray-700">
                                        Keep item on renewal?{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        required
                                        name="keepOnRenewal"
                                        value={renewalOption}
                                        onChange={onHandleChangeRenewal}
                                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                    >
                                        <option value="" disabled>
                                            Select an option
                                        </option>
                                        <option value="temporary">Temporary</option>
                                        <option value="permanent">Permanent</option>
                                    </select>
                                    <small className="block mt-1 text-gray-500">
                                        * Permanent keeps the item on renewal;
                                        Temporary will not persist.
                                    </small>
                                </div>

                                {/* Links toggle */}
                                {!displayOptionalFields.links ? (
                                    <div className="mt-4">
                                        <button
                                            type="button"
                                            onClick={toggleLinks}
                                            className="inline-flex items-center p-1 text-white bg-teal-500 rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none"
                                        >
                                            Add link
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mt-4">
                                            <button
                                                type="button"
                                                onClick={toggleLinks}
                                                className="inline-flex items-center p-1 text-white bg-red-500 rounded-sm shadow-sm hover:bg-red-600 focus:outline-none"
                                            >
                                                Hide link
                                            </button>
                                        </div>
                                        <div className="mt-2">
                                            <InputTextModal
                                                required={false}
                                                type="url"
                                                label="Link URL (optional)"
                                                name="url"
                                                onChange={handleChangeLink}
                                                value={newLinkUrl}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Actions */}
                                <div className="mt-6 sm:mt-8 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <ButtonModal
                                        name="Cancel"
                                        type="button"
                                        onClick={onHandleCancel}
                                        className="w-full px-4 py-2 text-gray-700 bg-white border rounded-md shadow-sm hover:bg-gray-50 focus:outline-none"
                                    />
                                    <ButtonModal
                                        name={isSaving ? "Saving..." : "Save"}
                                        type="submit"
                                        ref={completeButtonRef}
                                        disabled={
                                            isSaving ||
                                            permissionsLoading ||
                                            !canWriteSteps
                                        }
                                        className={`w-full inline-flex justify-center items-center px-4 py-2 bg-teal-500 text-white rounded-md shadow-sm hover:bg-teal-600 focus:outline-none transition ${
                                            isSaving
                                                ? "cursor-not-allowed bg-teal-300 hover:bg-teal-300"
                                                : ""
                                        }`}
                                    />
                                </div>
                            </form>
                        </Dialog.Panel>
                    </div>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default AddNewClientServiceSubscriptionTaskChecklistItem;
